/**
 * Normalize Centrifuge publication `ctx.data` without dropping the event `type`.
 *
 * Visitor widget publishes:
 *   { type: 'message.created', data: { type: 'TEXT', text, source }, createdAt, seq }
 * Inner `type: TEXT` is a content kind, not an event. Returning only the inner
 * object used to hide `message.created` from the operator UI.
 *
 * Dialog channel payloads (esp. Telegram) look like:
 *   { type: 'dialog.updated', data: { type: 'TELEGRAM', telegram: {...}, id, ... } }
 * Inner `type: TELEGRAM` / `WEB_CHAT` is a channel kind — must be preserved as
 * `channelType` when the event type overwrites `type`, otherwise the operator UI
 * loses Telegram-specific handling after WS updates / tab refetch.
 */

export function looksLikeEventType(value: unknown): boolean {
  const s = String(value ?? '').toLowerCase().trim()
  if (!s) return false
  return (
    s.includes('.') ||
    s.includes('message') ||
    s === 'typing' ||
    s.endsWith('.typing') ||
    s.startsWith('dialog.') ||
    s.startsWith('chat.') ||
    s.startsWith('subscription.')
  )
}

/** Dialog / channel kind (not a Centrifugo event name). */
export function looksLikeDialogChannelType(value: unknown): boolean {
  const s = String(value ?? '').trim().toUpperCase()
  return s === 'TELEGRAM' || s === 'WEB_CHAT'
}

function inheritOuterMeta(
  inner: Record<string, unknown>,
  outer: Record<string, unknown>,
): Record<string, unknown> {
  const pick = (key: string) =>
    outer[key] != null && inner[key] == null ? { [key]: outer[key] } : {}
  return {
    ...inner,
    ...pick('chatId'),
    ...pick('chat_id'),
    ...pick('id'),
    ...pick('messageId'),
    ...pick('message_id'),
    ...pick('eventId'),
    ...pick('seq'),
    ...pick('createdAt'),
    ...pick('created_at'),
    ...pick('requestId'),
    ...pick('request_id'),
    ...pick('toOperatorId'),
    ...pick('to_operator_id'),
    ...pick('fromOperatorId'),
    ...pick('from_operator_id'),
    ...pick('expiresAt'),
    ...pick('expires_at'),
    ...pick('from'),
    ...pick('source'),
  }
}

/** Keep TELEGRAM/WEB_CHAT when event type replaces inner `type`. */
function preserveChannelType(
  merged: Record<string, unknown>,
  innerType: unknown,
  outer: Record<string, unknown>,
): Record<string, unknown> {
  if (!looksLikeDialogChannelType(innerType)) {
    // Outer may already carry channelType from a previous hop.
    if (looksLikeDialogChannelType(outer.channelType) && merged.channelType == null) {
      return { ...merged, channelType: outer.channelType }
    }
    return merged
  }
  const channelType = String(innerType).trim().toUpperCase()
  return {
    ...merged,
    channelType:
      merged.channelType != null && looksLikeDialogChannelType(merged.channelType)
        ? merged.channelType
        : channelType,
  }
}

export function unwrapCentrifugePublication(raw: unknown): unknown {
  if (raw == null) return raw
  if (typeof raw === 'string') {
    try {
      return unwrapCentrifugePublication(JSON.parse(raw))
    } catch {
      return raw
    }
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) return raw

  const outer = raw as Record<string, unknown>
  const nestedRaw = outer.data ?? outer.payload
  if (nestedRaw == null || typeof nestedRaw !== 'object' || Array.isArray(nestedRaw)) {
    return outer
  }

  const inner = nestedRaw as Record<string, unknown>
  const outerType = outer.type ?? outer.event
  const innerType = inner.type ?? inner.event
  const outerEvent = looksLikeEventType(outerType)
  const innerEvent = looksLikeEventType(innerType)

  if (outerEvent && !innerEvent) {
    return preserveChannelType(
      inheritOuterMeta({ ...inner, type: outerType }, outer),
      innerType,
      outer,
    )
  }
  if (innerEvent && !outerEvent) {
    return preserveChannelType(inheritOuterMeta(inner, outer), outerType, outer)
  }
  if (outerEvent) {
    return preserveChannelType(
      inheritOuterMeta(
        {
          ...inner,
          type: outerType,
          ...(outer.event != null && inner.event == null ? { event: outer.event } : {}),
        },
        outer,
      ),
      innerType,
      outer,
    )
  }
  if (innerType != null) {
    return preserveChannelType(inheritOuterMeta(inner, outer), innerType, outer)
  }
  if (outerType != null) {
    return preserveChannelType(
      inheritOuterMeta({ ...inner, type: outerType }, outer),
      outerType,
      outer,
    )
  }
  return outer
}
